/* eslint-disable no-case-declarations */
/* eslint-disable max-lines */
import {
    useState, useEffect
} from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    TableCell as UITableCell
} from 'shared/components/ui/table';

// Define the types for the props
interface RecordsCellProps {
    row: any;
    column: {
        dataType: string;
        [key: string]: any;
    };
    value: any;
    onChange?: (value: any) => void;
    onBlur?: (value: any) => void;
    disableEditing?: boolean;
}

export function RecordsCell({
     column, value, onChange, onBlur, disableEditing = false
}: RecordsCellProps) {
    const [inputValue, setInputValue] = useState<string>(''); // For primitive types
    const [inputValueList, setInputValueList] = useState<string>(''); // For list input
    const [options, setOptions] = useState<string[]>([]); // For list type options
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputType, setInputType] = useState<string>(column.dataType || '');
    const [step, setStep] = useState<string>('any');

    // Update input value and type when props change
    useEffect(() => {
        const {
 inputType, step
} = setInputTypeMapper(column.dataType);
        setInputType(inputType);
        setStep(step);

        if (column.dataType === 'list') {
            if (Array.isArray(value)) {
                setOptions(value);
            } else if (typeof value === 'string') {
                setOptions([value]);
            } else {
                setOptions([]);
            }
        } else {
            setInputValue(value || (column.dataType === 'decimal' ? '0.00' : ''));
        }
    }, [value, column.dataType]);

    // Map data types to HTML input types
    function setInputTypeMapper(dataType = '') {
        switch (dataType.toLowerCase()) {
            case 'string':
                return {
 inputType: 'text',
step: 'any'
};
            case 'integer':
                return {
 inputType: 'number',
step: '1'
};
            case 'decimal':
                return {
 inputType: 'number',
step: 'any'
};
            case 'datetime':
                return {
 inputType: 'datetime-local',
step: 'any'
};
            case 'date':
                return {
 inputType: 'date',
step: 'any'
};
            case 'time':
                return {
 inputType: 'time',
step: 'any'
};
            case 'boolean':
            case 'enum':
                return {
 inputType: 'select',
step: 'any'
};
            case 'list':
            case 'equation':
                return {
 inputType: 'text',
step: 'any'
};
            default:
                return {
 inputType: 'text',
step: 'any'
};
        }
    }

    // Handle input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const newValue = e.target.value;
        if (column.dataType === 'integer' && newValue.includes('.')) return;

        if (column.dataType === 'list') {
            setInputValueList(newValue);
        } else {
            setInputValue(newValue);
            if (onChange) onChange(newValue);
        }
    };

    // Handle blur (non-list types only)
    const handleBlur = () => {
        if (column.dataType !== 'list') {
            if (onChange) onChange(inputValue);
            if (onBlur) onBlur(inputValue);
            setIsEditing(false);
        }
    };

    // Submit on Enter
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleBlur();
    };

    // Toggle edit mode
    const handleCellClick = () => {
        if (!disableEditing) {
            setIsEditing(true);
        }
    };

    // Add new list option
    const handleAddOption = () => {
        const newOption = inputValueList.trim();
        if (newOption && !options.includes(newOption)) {
            const updatedOptions = [...options, newOption];
            setOptions(updatedOptions);
            setInputValueList('');

            if (onChange) onChange(updatedOptions);
            if (onBlur) onBlur(updatedOptions);
            setIsEditing(false);
        }
    };

    // Remove option from list
    const handleRemoveOption = (optionToRemove: string) => {
        const updatedOptions = options.filter((option) => option !== optionToRemove);
        setOptions(updatedOptions);
        if (onChange) onChange(updatedOptions);
        if (onBlur) onBlur(updatedOptions);
        setIsEditing(false);
    };

    // Render display version of the cell (non-editing mode)
    const renderCell = () => {
        switch (column.dataType) {
            case 'date':
                if (!value) return '';
                const date = new Date(value);
                return isNaN(date.getTime()) ? value : date.toLocaleDateString();
            case 'datetime-local':
                if (!value) return '';
                const datetime = new Date(value);
                return isNaN(datetime.getTime()) ? value : datetime.toLocaleString();
            case 'currency':
                return value !== undefined && value !== null ? (
                    <span className="font-mono">${typeof value === 'number' ? value.toLocaleString() : value}</span>
                ) : '';
            case 'percent':
                return value !== undefined && value !== null ? (
                    <span className="font-mono">{(Number(value) * 100).toFixed(1)}%</span>
                ) : '';
            case 'number':
                return value !== undefined && value !== null ? (
                    <span className="font-mono">{typeof value === 'number' ? value.toLocaleString() : value}</span>
                ) : '';
            case 'list':
                return Array.isArray(value) ? value.join(', ') : value || '';
            default:
                return (value || '').toString();
        }
    };

    return (
        <UITableCell onClick={handleCellClick} className="">
            {isEditing ? (
                column.dataType === 'boolean' ? (
                    <select
                        value={inputValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="h-8 w-full"
                        autoFocus
                    >
                        <option value="">Select One Option</option>
                        <option value="True">True</option>
                        <option value="False">False</option>
                    </select>
                ) : column.dataType === 'list' ? (
                    <div className="space-y-2">
                        <Input
                            value={inputValueList}
                            onChange={handleChange}
                            placeholder="Enter new option"
                            className="w-full"
                            autoFocus
                        />
                        <button
                            onClick={handleAddOption}
                            className="bg-black text-white px-4 py-2 rounded"
                        >
                            Add
                        </button>

                        <div className="space-y-1">
                            {options.length > 0 ? (
                                <ul>
                                    {options.map((option, index) => (
                                        <li key={index} className="flex items-center justify-between">
                                            <span>{option}</span>
                                            <button
                                                onClick={() => handleRemoveOption(option)}
                                                className="text-red-500"
                                            >
                                                Remove
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>No options added yet.</p>
                            )}
                        </div>
                    </div>
                ) : (
                    <Input
                        value={inputValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        className="h-8 w-full"
                        autoFocus
                        type={inputType}
                        step={step}
                        placeholder={column.dataType === 'decimal' && !inputValue ? '0.00' : ''}
                    />
                )
            ) : (
                renderCell()
            )}
        </UITableCell>
    );
}
